import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Warehouse, Phone, CheckCircle2, Loader2, Timer, ShieldCheck } from "lucide-react";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Remplacement pare-brise",
  "Réparation d'impact",
  "Vitre latérale",
  "Lunette arrière",
  "Calibration caméra ADAS",
  "Diagnostic / je ne sais pas",
];
const SLOTS = ["13:00 – 15:00", "15:00 – 17:00", "17:00 – 19:00", "19:00 – 20:00"];

const initialForm = {
  intervention_type: "domicile",
  service: "",
  date: "",
  time_slot: "",
  name: "",
  phone: "",
  vehicle: "",
  address: "",
  message: "",
};

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors focus:border-[#D4AF37] focus:bg-white/[0.07]";

const formatDate = (d) =>
  new Date(`${d}T00:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

export const BookingSection = () => {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) return toast.error("Choisissez un service.");
    if (!form.date) return toast.error("Choisissez une date.");
    if (!form.time_slot) return toast.error("Choisissez un créneau horaire.");
    if (!/^[0-9+.\s-]{8,}$/.test(form.phone)) return toast.error("Numéro de téléphone invalide.");
    if (form.intervention_type === "domicile" && form.address.trim().length < 5)
      return toast.error("Indiquez l'adresse d'intervention.");
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.intervention_type === "atelier") payload.address = "";
      const { data } = await axios.post(`${API}/bookings`, payload);
      setConfirmation(data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Erreur lors de l'envoi. Réessayez ou appelez-nous.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="rendez-vous" data-testid="booking-section" className="relative overflow-hidden bg-[#050505] py-24 lg:py-32">
      <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-[#D4AF37]/8 blur-[140px]" />
      <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-5 lg:px-10">
        <Reveal className="lg:col-span-2">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D4AF37]">Prise de rendez-vous</p>
          <h2 className="mt-4 font-display text-4xl uppercase leading-tight text-white sm:text-5xl lg:text-6xl">
            Réservez en <span className="text-gold-gradient">2 minutes</span>
          </h2>
          <p className="mt-6 text-sm leading-relaxed text-zinc-400 sm:text-base">
            Choisissez votre créneau, on s'occupe du reste. Nous vous rappelons pour confirmer votre intervention et
            gérer le dossier avec votre assurance.
          </p>
          <ul className="mt-8 space-y-4">
            <li className="flex items-center gap-3 text-sm text-zinc-300">
              <Timer className="h-5 w-5 shrink-0 text-[#D4AF37]" /> Réponse en moins de 30 minutes aux heures ouvrées
            </li>
            <li className="flex items-center gap-3 text-sm text-zinc-300">
              <ShieldCheck className="h-5 w-5 shrink-0 text-[#D4AF37]" /> Dossier assurance géré directement par nos soins
            </li>
          </ul>
          <div className="mt-10 rounded-2xl border border-[#D4AF37]/30 bg-[#0D0D0F] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500">Une urgence ?</p>
            <a
              href="tel:0602259560"
              data-testid="booking-phone-link"
              className="mt-3 flex items-center gap-3 font-display text-3xl tracking-wider text-white transition-colors hover:text-[#E6CA65]"
            >
              <Phone className="h-6 w-6 text-[#D4AF37]" /> 06 02 25 95 60
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <div className="rounded-2xl border border-white/10 bg-[#0D0D0F] p-6 sm:p-10">
            <AnimatePresence mode="wait">
              {confirmation ? (
                <motion.div
                  key="confirmation"
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  data-testid="booking-confirmation"
                  className="flex flex-col items-start gap-5"
                >
                  <CheckCircle2 className="h-14 w-14 text-[#D4AF37]" />
                  <h3 className="font-display text-4xl uppercase text-white">Demande envoyée !</h3>
                  <p className="text-sm leading-relaxed text-zinc-300 sm:text-base">
                    Merci {confirmation.name}. Nous vous rappelons très vite au{" "}
                    <span className="font-semibold text-white">{confirmation.phone}</span> pour confirmer votre
                    rendez-vous du <span className="font-semibold text-[#E6CA65]">{formatDate(confirmation.date)}</span>{" "}
                    ({confirmation.time_slot}).
                  </p>
                  <div className="w-full rounded-xl border border-white/10 bg-white/5 p-5 text-sm text-zinc-300">
                    <p>
                      <span className="text-zinc-500">Service :</span> {confirmation.service}
                    </p>
                    <p className="mt-1">
                      <span className="text-zinc-500">Lieu :</span>{" "}
                      {confirmation.intervention_type === "domicile"
                        ? `À domicile — ${confirmation.address}`
                        : "Atelier — 12 Chemin Tillon, 33140 Villenave d'Ornon"}
                    </p>
                    <p className="mt-1">
                      <span className="text-zinc-500">Référence :</span>{" "}
                      <span className="font-mono text-[#E6CA65]">{confirmation.id.slice(0, 8).toUpperCase()}</span>
                    </p>
                  </div>
                  <button
                    data-testid="booking-new-button"
                    onClick={() => {
                      setForm(initialForm);
                      setConfirmation(null);
                    }}
                    className="rounded-full border border-[#D4AF37]/40 px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#E6CA65] transition-colors hover:bg-[#D4AF37]/10"
                  >
                    Faire une autre demande
                  </button>
                </motion.div>
              ) : (
                <motion.form
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.98 }}
                  transition={{ duration: 0.4 }}
                  onSubmit={submit}
                  data-testid="booking-form"
                  className="space-y-8"
                >
                  <div>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                      Lieu d'intervention
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { id: "domicile", icon: Home, title: "À domicile", sub: "Déplacement gratuit" },
                        { id: "atelier", icon: Warehouse, title: "À l'atelier", sub: "Villenave d'Ornon" },
                      ].map((opt) => (
                        <button
                          type="button"
                          key={opt.id}
                          data-testid={`booking-type-${opt.id}`}
                          onClick={() => setForm((f) => ({ ...f, intervention_type: opt.id }))}
                          className={`rounded-xl border p-4 text-left transition-all duration-300 ${
                            form.intervention_type === opt.id
                              ? "border-[#D4AF37] bg-[#D4AF37]/10"
                              : "border-white/10 bg-white/5 hover:border-white/25"
                          }`}
                        >
                          <opt.icon
                            className={`h-5 w-5 ${form.intervention_type === opt.id ? "text-[#D4AF37]" : "text-zinc-400"}`}
                          />
                          <p className="mt-2 text-sm font-semibold text-white">{opt.title}</p>
                          <p className="text-xs text-zinc-500">{opt.sub}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">Service souhaité</p>
                    <div className="flex flex-wrap gap-2">
                      {SERVICES.map((s) => (
                        <button
                          type="button"
                          key={s}
                          data-testid={`booking-service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                          onClick={() => setForm((f) => ({ ...f, service: s }))}
                          className={`rounded-full border px-4 py-2 text-xs font-medium transition-all duration-300 ${
                            form.service === s
                              ? "border-[#D4AF37] bg-[#D4AF37] text-black"
                              : "border-white/15 bg-white/5 text-zinc-300 hover:border-[#D4AF37]/50"
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="grid gap-6 sm:grid-cols-2">
                    <div>
                      <label htmlFor="booking-date" className="mb-3 block text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">
                        Date souhaitée
                      </label>
                      <input
                        id="booking-date"
                        data-testid="booking-date-input"
                        type="date"
                        min={today}
                        value={form.date}
                        onChange={set("date")}
                        className={inputCls}
                      />
                    </div>
                    <div>
                      <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-zinc-400">Créneau horaire</p>
                      <div className="grid grid-cols-2 gap-2">
                        {SLOTS.map((slot) => (
                          <button
                            type="button"
                            key={slot}
                            data-testid={`booking-slot-${slot.slice(0, 2)}h`}
                            onClick={() => setForm((f) => ({ ...f, time_slot: slot }))}
                            className={`rounded-lg border px-2 py-2 text-xs font-medium transition-all duration-300 ${
                              form.time_slot === slot
                                ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#E6CA65]"
                                : "border-white/10 bg-white/5 text-zinc-400 hover:border-white/25"
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <input
                      data-testid="booking-name-input"
                      placeholder="Nom & prénom *"
                      required
                      value={form.name}
                      onChange={set("name")}
                      className={inputCls}
                    />
                    <input
                      data-testid="booking-phone-input"
                      placeholder="Téléphone *"
                      required
                      type="tel"
                      value={form.phone}
                      onChange={set("phone")}
                      className={inputCls}
                    />
                    <input
                      data-testid="booking-vehicle-input"
                      placeholder="Véhicule (modèle ou immatriculation) *"
                      required
                      value={form.vehicle}
                      onChange={set("vehicle")}
                      className={`${inputCls} sm:col-span-2`}
                    />
                    {form.intervention_type === "domicile" && (
                      <input
                        data-testid="booking-address-input"
                        placeholder="Adresse d'intervention *"
                        value={form.address}
                        onChange={set("address")}
                        className={`${inputCls} sm:col-span-2`}
                      />
                    )}
                    <textarea
                      data-testid="booking-message-input"
                      placeholder="Précisions (position de l'impact, assurance, etc.)"
                      rows={3}
                      value={form.message}
                      onChange={set("message")}
                      className={`${inputCls} resize-none sm:col-span-2`}
                    />
                  </div>

                  <button
                    type="submit"
                    data-testid="booking-submit-button"
                    disabled={loading}
                    className="flex w-full items-center justify-center gap-3 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold uppercase tracking-widest text-black transition-colors hover:bg-[#F3E5AB] disabled:opacity-60"
                  >
                    {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                    {loading ? "Envoi en cours..." : "Confirmer ma demande de rendez-vous"}
                  </button>
                  <p className="text-center text-xs text-zinc-500">
                    Sans engagement — nous confirmons ensemble par téléphone avant toute intervention.
                  </p>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
